import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '@/lib/api.ts'
import { Search, Package } from 'lucide-react'
import {
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  Input,
  PageHeader,
  Skeleton,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  currencyColumn,
  dateColumn,
  textColumn,
  type ColumnDef,
} from '@/components/ui'

interface AlsoProduct {
  sku: string
  name: string
  vendor: string
  category?: string
  price?: number
  currency?: string
  available: boolean
  description?: string
}

interface AlsoSubscription {
  id: string
  productName: string
  sku: string
  quantity: number
  status: 'active' | 'suspended' | 'cancelled'
  renewalDate?: string
  monthlyPrice?: number
}

const subscriptionColumns: ColumnDef<AlsoSubscription, unknown>[] = [
  textColumn<AlsoSubscription>('productName', 'Produkt'),
  {
    accessorKey: 'sku',
    header: 'SKU',
    cell: ({ getValue }) => (
      <span className="font-mono text-caption text-gray-500">{getValue() as string}</span>
    ),
  },
  {
    accessorKey: 'quantity',
    header: 'Menge',
    size: 80,
    cell: ({ getValue }) => <span className="tabular-nums">{getValue() as number}</span>,
  },
  currencyColumn<AlsoSubscription>('monthlyPrice', 'Monatlich', { size: 120 }),
  dateColumn<AlsoSubscription>('renewalDate', 'Erneuerung', { size: 120 }),
  {
    accessorKey: 'status',
    header: 'Status',
    size: 120,
    cell: ({ getValue }) => <StatusBadge status={getValue() as string} />,
  },
]

export function MarketplacePage() {
  const [search, setSearch] = useState('')
  const [activeTab, setActiveTab] = useState<'products' | 'subscriptions'>('subscriptions')

  const products = useQuery<AlsoProduct[]>({
    queryKey: ['also-products', search],
    queryFn: () => api.get('/marketplace/products', { params: { q: search } }).then((r) => r.data),
    enabled: activeTab === 'products',
    retry: false,
  })

  const subscriptions = useQuery<AlsoSubscription[]>({
    queryKey: ['also-subscriptions'],
    queryFn: () => api.get('/marketplace/subscriptions').then((r) => r.data),
    enabled: activeTab === 'subscriptions',
    retry: false,
  })

  return (
    <div className="space-y-4">
      <PageHeader
        title="ALSO Marketplace"
        description="Produkte, Bestellungen und Abonnements"
      />

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'products' | 'subscriptions')}>
        <TabsList>
          <TabsTrigger value="subscriptions">Abonnements</TabsTrigger>
          <TabsTrigger value="products">Produktkatalog</TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="space-y-4">
          <Input
            leadingIcon={<Search />}
            placeholder="Produkt suchen …"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />

          {products.isLoading ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-32" />
              ))}
            </div>
          ) : products.isError ? (
            <Card>
              <Card.Body>
                <ErrorState
                  title="ALSO Marketplace nicht verfügbar"
                  description="Entweder nicht konfiguriert oder Verbindungsfehler."
                  onRetry={() => products.refetch()}
                />
              </Card.Body>
            </Card>
          ) : (products.data ?? []).length === 0 ? (
            <Card>
              <Card.Body>
                <EmptyState title="Keine Produkte gefunden" description="Versuche einen anderen Suchbegriff." />
              </Card.Body>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {(products.data ?? []).map((p) => (
                <Card key={p.sku}>
                  <Card.Body>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-body font-semibold text-gray-900 truncate">{p.name}</p>
                        <p className="text-caption text-gray-500 mt-0.5">
                          {p.vendor} · {p.sku}
                        </p>
                      </div>
                      <Package
                        className={`w-5 h-5 flex-shrink-0 ${p.available ? 'text-green-500' : 'text-gray-300'}`}
                      />
                    </div>
                    {p.description ? (
                      <p className="text-caption text-gray-500 mt-2 line-clamp-2">{p.description}</p>
                    ) : null}
                    {p.price != null ? (
                      <p className="text-body font-bold text-gray-900 mt-3">
                        {p.price.toLocaleString('de', { minimumFractionDigits: 2 })}{' '}
                        {p.currency ?? 'EUR'}
                      </p>
                    ) : null}
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="subscriptions">
          <DataTable<AlsoSubscription>
            data={subscriptions.data ?? []}
            columns={subscriptionColumns}
            getRowId={(s) => s.id}
            loading={subscriptions.isLoading}
            error={
              subscriptions.isError
                ? 'ALSO Marketplace nicht konfiguriert oder Verbindungsfehler.'
                : null
            }
            onRetry={() => subscriptions.refetch()}
            emptyState={<EmptyState title="Keine Abonnements gefunden" />}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}
